import { Button, Line, Spinner, Typography, useMobileAndTablet } from '@sonnen/web-ui';
import clsx from 'clsx';
import { useAppContext } from '../../../AppContext/AppContext';
import CheckmarkLines from '../../../components/CheckmarkLines/CheckmarkLines';
import { AppRoute } from '../../../constants';
import { useTopScrollNavigate } from '../../../hooks/useTopScrollNavigate';
import { useTranslation } from '../../../i18n/i18n';
import { useOfferContext } from '../../../OfferContext/OfferContext';
import { OrderFlow } from '../../../types';
import { formatCurrency } from '../../../utils/formatting';
import { getSelectedTariffConfiguration } from '../../../utils/utils';
import styles from './EnergyTariffCard.module.css';
import EnergyTariffLogo from './EnergyTariffLogo/EnergyTariffLogo';
import TariffBenefitCard from './TariffBenefitCard/TariffBenefitCard';

type EnergyCardProps = {
  flow: OrderFlow.energyDynamic | OrderFlow.energyFix;
};

const i18n = 'tariffsComparison';

const EnergyTariffCard = ({ flow }: EnergyCardProps) => {
  const { t } = useTranslation();
  const tablet = useMobileAndTablet();
  const { setOrderFlow, modules } = useAppContext();
  const navigate = useTopScrollNavigate();
  const { offer, offerLoading } = useOfferContext();
  const { energyDynamic, energyFix } = offer || {};

  const isEnergyFixCard = flow === OrderFlow.energyFix;

  if (isEnergyFixCard && !energyFix) return false;

  const costWithPriceOptimization =
    energyDynamic?.configurationWithBatteryOptimizationModule?.averagePrices
      .expectedPaymentGrossPerMonth;
  const costWithoutPriceOptimization =
    energyDynamic?.configurationWithoutBatteryOptimizationModule?.averagePrices
      .expectedPaymentGrossPerMonth;

  const costWithoutPriceOptimizationFormatted = formatCurrency(
    costWithoutPriceOptimization,
    'euro',
    false,
  );

  const energyDynamicCosts = costWithPriceOptimization
    ? `${formatCurrency(costWithPriceOptimization, 'euro', false)}-${costWithoutPriceOptimizationFormatted}`
    : costWithoutPriceOptimizationFormatted;

  const estimatedCosts =
    isEnergyFixCard && energyFix
      ? formatCurrency(energyFix.configuration?.expectedPaymentGrossPerMonth, 'euro', false)
      : energyDynamicCosts;

  const configuration = getSelectedTariffConfiguration(offer, modules, flow);
  const switchingBonusAmount = configuration?.tariffChangeFlatXSonnenHomeOneTimeBenefitAmount;
  const workingPrice = isEnergyFixCard ? configuration?.purchaseTariffGrossPerKwh : null;

  const logoSize = tablet ? 'small' : 'large';

  return (
    <div className={clsx(styles.energyTariffCard)} data-testid={`${flow}-card`}>
      <div className={styles.content}>
        <div>
          <div className={styles.logoWithTitle}>
            <EnergyTariffLogo flow={flow} size={logoSize} />
            <Typography.H2>{t(`${i18n}.${flow}.title`)}</Typography.H2>
          </div>

          <div className={styles.mainText}>
            <p>
              <Typography.Body1>{t(`${i18n}.${flow}.text`)}</Typography.Body1>
            </p>
            <CheckmarkLines list={t(`${i18n}.${flow}.bulletPoints`).split('\n')} />
          </div>

          {(switchingBonusAmount || workingPrice) && (
            <div className={styles.benefitCards}>
              <div className={styles.benefitTitle}>
                <Typography.H5>{t('benefitCard.title')}</Typography.H5>
              </div>
              <div className={styles.benefitSectionsRow}>
                {switchingBonusAmount && (
                  <TariffBenefitCard
                    title={t('offer.welcomeBonus')}
                    value={formatCurrency(switchingBonusAmount, 'euro')}
                    dataTestId={`${flow}-bonus-card`}
                  />
                )}
                {workingPrice && (
                  <TariffBenefitCard
                    title={t(
                      'offer.accordion.purchaseTariffGrossPerKwhLabelWithoutYear.energyFix.label',
                    )}
                    value={`${formatCurrency(workingPrice, 'ct')}/kWh`}
                    dataTestId="ef-working-price-card"
                  />
                )}
              </div>
            </div>
          )}

          <Line />
        </div>

        <div className={styles.priceAndButton}>
          <div className={styles.costs}>
            <Typography.H4>{t(`${i18n}.${flow}.estimatedCosts`)}</Typography.H4>

            <div className={styles.amountAndCurrency}>
              {offerLoading ? (
                <div className={styles.spinnerWrapper}>
                  <Spinner size="sm" accessibilityLabel={t('offer.loading')} />
                </div>
              ) : (
                <Typography.Number3>{estimatedCosts}</Typography.Number3>
              )}
              <Typography.Label1>{` €/${t('offer.month')}`}</Typography.Label1>
            </div>
          </div>

          <Button
            size="large"
            iconRight="ArrowRight"
            label={t(`${i18n}.${flow}.button`)}
            dataTestId={`${flow}-card-btn`}
            onClick={() => {
              setOrderFlow(flow);
              navigate(AppRoute.TARIFF_OVERVIEW);
            }}
            disabled={!offer}
          />
        </div>
      </div>
    </div>
  );
};

export default EnergyTariffCard;
